import { useEffect, useState } from 'react';
import { Link, Redirect, Route, useLocation, useParams, Switch } from 'react-router-dom';
import { Button, Container, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { CircularIndeterminate } from '../FormWidgets/Others';
import { SettingsTabs } from './ControllerPage/SettingsTabs.js';
import ZoneMenu from '../Menus/ZoneMenu.js';
import ControllerNavigation from '../Menus/ControllerNavigation';
import { Error404 } from '../components/Error404';
import FirmwareRecoveryButton from '../FormWidgets/FirmwareRecoveryButton';
import SystemSettings from '../Tabs/SystemSettings';
import LoggingTab from '../Tabs/LoggingTab';
import { canOpenFirmwareRecovery } from '../Utils/system_settings_utils';
import { supportsDatabaseHistory } from '../Utils/database_history_utils';
import usePreviewFeatures from '../Hooks/usePreviewFeatures';

import getStateManager from '../StateManager.js';
import { useSnapshot } from 'valtio';
import { isZoned } from '../Utils/utils.js';

const FIRMWARE_RECOVERY_DELAY_MS = 20000;

export function CheckForControllerExists({ ...other }) {
  const { id } = useParams();
  const stateManager = getStateManager();
  const { controllers_loaded } = useSnapshot(stateManager);
  if (!controllers_loaded) {
    return <CircularIndeterminate />;
  } else {
    if (stateManager.getControllerById(id) != null) {
      return <ControllerPage key={id} {...other} />;
    } else {
      return <Error404 />;
    }
  }
}

export default function ControllerPage({ ...other }) {
  const { id } = useParams();
  const location = useLocation();
  const { t } = useTranslation();

  const stateManager = getStateManager();
  const controllerState = stateManager.getControllerById(id);
  const controller = useSnapshot(controllerState);
  const previewEnabled = usePreviewFeatures();
  const showHistory = supportsDatabaseHistory(controller, undefined, previewEnabled);
  // Resync clears received categories after setting awaiting_values. Observe
  // that guard synchronously so nested form subscriptions cannot render first.
  const connection = useSnapshot(controllerState.connection, { sync: true });
  if (controller.type === 5155 && showHistory && location.pathname === `/controller/${id}/zoneoverview`) {
    return <Redirect to={`/controller/${id}/database_history`} />;
  }
  // Server history is independent of live controller settings, like the
  // explicit recovery route below. It must also open after an offline reload.
  if (location.pathname === `/controller/${id}/database_history`) {
    if (!showHistory) return <Error404 />;
    if (controller.type !== 5155) return (
      <Route path="/controller/:id/:tab_id">
        <SettingsTabs url={`/controller/${id}`} path="/controller/:id"
          parent={controller} controller={controller} />
      </Route>
    );
    return <>
      <Route path="/controller/:id/:menu">
        <ControllerNavigation controller={controller} />
      </Route>
      <Container style={{ marginTop: '2em', marginBottom: '2em' }}>
        <LoggingTab key={id} />
      </Container>
    </>;
  }
  // Explicit recovery must remain available while migration firmware sends only
  // partial settings. Keep it independent of telemetry freshness and resync.
  if (
    location.pathname === `/controller/${id}/system` &&
    new URLSearchParams(location.search).get('recovery') === '1'
  ) {
    if (!canOpenFirmwareRecovery(controller)) return <Error404 />;
    return (
      <Container style={{ marginTop: '2em' }}>
        <Typography variant="h6">{t('firmware_recovery')}</Typography>
        <Typography paragraph>{t('firmware_recovery_info')}</Typography>
        <Button component={Link} to={`/controller/${id}/system`} color="primary">
          {t('exit_firmware_recovery')}
        </Button>
        <SystemSettings key={id} recoveryMode={true} />
      </Container>
    );
  }
  // Reconnect rebuilds received settings. Unmount forms until full settings
  // arrive so their local state cannot initialize from stale or incomplete data.
  const awaitingValues = connection.awaiting_values;
  const hasSettings = !_.isEmpty(controller?.settings) || !_.isEmpty(controller?.zones);
  if (awaitingValues && controller.connection.status !== 'issues') {
    return <ControllerLoading key={id} controller={controller} />;
  }
  // An active controller is also yellow before its first values arrive. Reuse
  // the loading grace period before replacing the selected tab with recovery.
  if (
    controller.connection.status === 'issues' &&
    (awaitingValues || !hasSettings) &&
    canOpenFirmwareRecovery(controller) &&
    location.pathname !== `/controller/${id}/system`
  ) {
    return <ControllerLoading key={id} controller={controller} redirectToSystem={true} />;
  }
  return (
    <>
      {!awaitingValues && hasSettings ? (
        <>
          {isZoned(controller) ? (
            <Route path={`/controller/:id/:menu?/:zone_id?/:tab_id?`}>
              <ZoneMenu />
            </Route>
          ) : (
            <Switch>
              <Route path={`/controller/:id/:tab_id?`}>
                <SettingsTabs
                  url={`/controller/${id}`}
                  path={'/controller/:id'}
                  parent={controller}
                  controller={controller}
                />
              </Route>
              <Route path="*">
                <Error404 />
              </Route>
            </Switch>
          )}
        </>
      ) : (
        <>
          {!(controller?.connection?.status === 'issues') && (
            <ControllerLoading key={id} controller={controller} />
          )}
          {controller?.connection?.status === 'issues' &&
            (isZoned(controller) ? (
              <Route path={`/controller/:id/:menu?/:zone_id?/:tab_id?`}>
                <ZoneMenu recoveryMode={true} />
              </Route>
            ) : (
              <Route path={`/controller/:id/:tab_id?`}>
                <SettingsTabs
                  url={`/controller/${id}`}
                  path={'/controller/:id'}
                  parent={controller}
                  controller={controller}
                  recoveryMode={true}
                />
              </Route>
            ))}
        </>
      )}
    </>
  );
}

function ControllerLoading({ controller, redirectToSystem = false }) {
  const [showRecovery, setShowRecovery] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShowRecovery(true), FIRMWARE_RECOVERY_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  if (showRecovery && redirectToSystem) {
    return <Redirect to={`/controller/${controller.id}/system`} />;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1em',
        marginTop: '3em'
      }}>
      <CircularIndeterminate />
      {showRecovery && <FirmwareRecoveryButton controller={controller} />}
    </div>
  );
}
